import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import CommonTable, { CommonTableProps } from "components/CommonTable";
import StaffTaskRes from "types/res/staffTask/StaffTaskRes";
import {
  addStaffTask,
  deleteStaffTask,
  fetchStaffTask,
  updateStaffTask,
} from "redux/actions/staffTask";
import { fetchStaff } from "redux/actions/staff";
import TaskPriority from "types/enum/TaskPriority";
import TaskStatus from "types/enum/TaskStatus";
import DateTimeUtils, { FORMAT_TYPE } from "utils/DateTimeUtils";
import { FormType } from "components/FormField";
import UpdateStaffTaskReq from "types/req/staffTask/UpdateStaffTaskReq";
import CreateStaffTaskReq from "types/req/staffTask/CreateStaffTaskReq";
import EnumUtils from "../../utils/EnumUtils";

const StaffTask: React.FC = () => {
  const { t } = useTranslation("task");
  const dispatch = useDispatch();
  const companyId = useSelector((state) => state.account.staff.companyId);
  const staffList = useSelector((state) => state.staff);
  const staffTasks = useSelector((state) => state.staffTask);
  useEffect(() => {
    dispatch(fetchStaff(companyId));
    dispatch(fetchStaffTask(companyId));
  }, [dispatch, companyId]);
  const headers: { key: keyof StaffTaskRes; label: string }[] = [
    { key: "name", label: t("task_name") },
    { key: "summary", label: t("summary") },
    { key: "priority", label: t("priority") },
    { key: "status", label: t("status") },
    { key: "deadline", label: t("deadline") },
    { key: "registeredBy", label: t("registered_by") },
    { key: "assignee", label: t("assignee") },
  ];
  const forms = [
    {
      label: t("task_name"),
      key: "name",
      type: FormType.Text,
    },
    {
      label: t("summary"),
      key: "summary",
      type: FormType.TextArea,
    },
    {
      label: t("priority"),
      key: "priority",
      type: FormType.Select,
      options: Object.entries(TaskPriority).map(([key, value]) => ({
        id: key,
        name: value,
      })),
    },
    {
      label: t("status"),
      key: "status",
      type: FormType.Select,
      options: Object.entries(TaskStatus).map(([key, value]) => ({
        id: key,
        name: value,
      })),
    },
    {
      label: t("deadline"),
      key: "deadline",
      type: FormType.Date,
    },
    {
      label: t("registered_by"),
      key: "registeredById",
      type: FormType.Select,
      options: staffList.map((s) => ({
        id: s.staffId,
        name: s.name,
      })),
    },
    {
      label: t("assignee"),
      key: "assigneeId",
      type: FormType.Select,
      options: staffList.map((s) => ({ id: s.staffId, name: s.name })),
    },
  ];
  return (
    <StaffTaskTable
      title={t("tasks")}
      formId="staffTaskForm"
      forms={forms}
      addFunc={(formData) => addStaffTask(companyId, formData)}
      updateFunc={(formData) => updateStaffTask(companyId, formData)}
      deleteFunc={(item) =>
        deleteStaffTask(companyId, {
          staffTaskId: item?.staffTaskId,
        })
      }
      rows={headers}
      data={staffTasks}
      addType={CreateStaffTaskReq}
      updateType={UpdateStaffTaskReq}
      values={[
        (s) => s.name,
        (s) => s.summary,
        (s) => EnumUtils.mapToEnum(TaskPriority, s.priority) || t("not_set"),
        (s) => EnumUtils.mapToEnum(TaskStatus, s.status) || t("not_set"),
        (s) =>
          DateTimeUtils.toFormatAsLocalTimezone(
            s.deadline,
            FORMAT_TYPE.YEAR_DAY
          ),
        (s) => s.registeredBy.name || "",
        (s) => s.assignee.name || "",
      ]}
    />
  );
};

const StaffTaskTable = styled<
  React.FC<
    CommonTableProps<StaffTaskRes, CreateStaffTaskReq, UpdateStaffTaskReq>
  >
>(CommonTable)`
  margin-top: 24px;
`;

export default StaffTask;
